// Where the venue switcher takes you (spec/13 §2): the same page at the place you chose. Switching from a venue's
// Stations to another venue opens that venue's Stations, a pricing tab stays on its tab, and a settings group on its
// group. What names a record of the place you are leaving — a stock count — is never carried: the section's own page
// opens instead. A page the other place has no counterpart for, or one the member may not open there, opens the
// place's home. Search and hash are dropped: they hold a record (a shift, an item) or a filter of the old place.
//
// Pure: the router's path in, the router's `{ to, params }` out, so the table below is the whole of it and a test reads
// it row by row (`src/__tests__/venue-switcher.test.tsx`).

/** A place the console is about: a venue (`/v/{slug}`) or an organisation's own area (`/o/{slug}`). */
export type PlaceKind = 'venue' | 'organization';

export interface SwitchPlace {
  readonly kind: PlaceKind;
  readonly slug: string;
}

/** The router's own form: a route template and its params, as `navigate` takes it. */
export interface SwitchRoute {
  readonly to: string;
  readonly params: Readonly<Record<string, string>>;
}

interface RouteShape {
  /** The path below the place's root, as the router writes it (`pricing/$tab`); `''` is the place's home. */
  readonly path: string;
  /** The sidebar section the page belongs to (`sections.ts`), to ask whether the member may open it at the target. */
  readonly section?: string;
  /** A page whose params name a record of this place: switching opens this path instead (the section's own page). */
  readonly fallback?: string;
  /** The same section at the other kind of place, by its path there; absent, a switch across opens the home. */
  readonly across?: string;
}

const VENUE_ROUTES: readonly RouteShape[] = [
  { path: '', section: 'floor' },
  { path: 'floor/arrange', section: 'floor' },
  { path: 'sessions', section: 'floor' },
  { path: 'sessions/offline', section: 'floor' },
  { path: 'sell', section: 'sell' },
  { path: 'orders', section: 'orders' },
  { path: 'kitchen', section: 'kitchen' },
  { path: 'menu', section: 'kitchen' },
  { path: 'stock', section: 'stock' },
  { path: 'stock/$tab', section: 'stock' },
  { path: 'stock/counts/$countId', section: 'stock', fallback: 'stock' },
  { path: 'reports', section: 'reports' },
  { path: 'players', section: 'players', across: 'players' },
  { path: 'wallet', section: 'players', across: 'players' },
  { path: 'shifts', section: 'shifts' },
  { path: 'staff', section: 'staff', across: 'staff' },
  { path: 'pricing', section: 'pricing', across: 'pricing' },
  { path: 'pricing/$tab', section: 'pricing', across: 'pricing' },
  { path: 'passes', section: 'pricing', across: 'pricing' },
  { path: 'library', section: 'library' },
  { path: 'stations', section: 'stations' },
  { path: 'settings', section: 'settings', across: 'settings' },
  { path: 'settings/$group', section: 'settings', across: 'settings' },
  { path: 'account', across: 'account' },
  // Setup (spec/13 §13.1) is no sidebar section: each Setup page answers for itself what the member may read.
  { path: 'setup', across: 'setup' },
  { path: 'soon/$section' },
];

const ORGANIZATION_ROUTES: readonly RouteShape[] = [
  { path: '' },
  { path: 'venues', section: 'venues' },
  { path: 'players', section: 'players', across: 'players' },
  { path: 'pricing', section: 'pricing', across: 'pricing' },
  { path: 'pricing/$tab', section: 'pricing', across: 'pricing' },
  { path: 'staff', section: 'staffAndRoles', across: 'staff' },
  { path: 'settings', section: 'settings', across: 'settings' },
  { path: 'settings/$group', section: 'settings', across: 'settings' },
  { path: 'audit', section: 'audit' },
  { path: 'account', across: 'account' },
  { path: 'setup', across: 'setup' },
  { path: 'soon/$section' },
];

const ROUTES: Readonly<Record<PlaceKind, readonly RouteShape[]>> = { venue: VENUE_ROUTES, organization: ORGANIZATION_ROUTES };
const ROOT: Readonly<Record<PlaceKind, { readonly to: string; readonly param: string }>> = {
  venue: { to: '/v/$venueSlug', param: 'venueSlug' },
  organization: { to: '/o/$orgSlug', param: 'orgSlug' },
};

const segmentsOf = (path: string): string[] => path.split('/').filter((segment) => segment !== '');

/** Decoded as the router decodes it; a malformed escape is kept as it was written. */
function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The place a path is at, and the rest of it below the place's root — or `undefined` off a venue or organisation. */
export function placeOfPath(pathname: string): { readonly place: SwitchPlace; readonly rest: readonly string[] } | undefined {
  const [area, slug, ...rest] = segmentsOf(pathname);
  if (slug === undefined) return undefined;
  if (area === 'v') return { place: { kind: 'venue', slug: decode(slug) }, rest: rest.map(decode) };
  if (area === 'o') return { place: { kind: 'organization', slug: decode(slug) }, rest: rest.map(decode) };
  return undefined;
}

/** The route shape a path's rest is, and the params it holds. */
function match(kind: PlaceKind, rest: readonly string[]): { readonly shape: RouteShape; readonly params: Record<string, string> } | undefined {
  for (const shape of ROUTES[kind]) {
    const pattern = segmentsOf(shape.path);
    if (pattern.length !== rest.length) continue;
    const params: Record<string, string> = {};
    const fits = pattern.every((part, i) => {
      const actual = rest[i] as string;
      if (part.startsWith('$')) {
        params[part.slice(1)] = actual;
        return true;
      }
      return part === actual;
    });
    if (fits) return { shape, params };
  }
  return undefined;
}

function routeAt(target: SwitchPlace, path: string, params: Readonly<Record<string, string>>): SwitchRoute {
  const root = ROOT[target.kind];
  const kept = Object.fromEntries(segmentsOf(path).filter((part) => part.startsWith('$')).map((part) => [part.slice(1), params[part.slice(1)] ?? '']));
  return { to: path === '' ? root.to : `${root.to}/${path}`, params: { [root.param]: target.slug, ...kept } };
}

/** The place's home: a venue's Floor, an organisation's own area. */
export function homeOf(target: SwitchPlace): SwitchRoute {
  return routeAt(target, '', {});
}

/**
 * The route to open at `target` for the page at `pathname`. `opens` says whether the member may open a sidebar section
 * at the target (`sections.ts`); without it every section counts as open — each screen still asks the server.
 */
export function switchRoute(pathname: string, target: SwitchPlace, opens?: (section: string) => boolean): SwitchRoute {
  const from = placeOfPath(pathname);
  if (from === undefined) return homeOf(target);
  const found = match(from.place.kind, from.rest);
  if (found === undefined) return homeOf(target);
  const { shape, params } = found;

  const path = from.place.kind === target.kind ? (shape.fallback ?? shape.path) : shape.across;
  if (path === undefined) return homeOf(target);
  const there = ROUTES[target.kind].find((candidate) => candidate.path === path);
  if (there === undefined) return homeOf(target);
  if (there.section !== undefined && opens !== undefined && !opens(there.section)) return homeOf(target);
  // Across kinds only the section's root is carried: a tab or a group of one kind of place need not exist at the other.
  return routeAt(target, path, from.place.kind === target.kind ? params : {});
}

/** The path a route stands for, with its params written in — what the address bar shows once it opens. */
export function pathOfRoute(route: SwitchRoute): string {
  return route.to.replace(/\$([A-Za-z]+)/g, (_, name: string) => encodeURIComponent(route.params[name] ?? ''));
}
